import { useEffect, useState } from "react";

export const useAssetIcon = (assetName?: string | null) => {
    const [icon, setIcon] = useState<string | null>(null);

    useEffect(() => {
        let canceled = false;
        if (!assetName) return;

        const getBaseTokenFromLP = (lpName: string): string | null => {
            const parts = lpName.split('-');
            return parts.length >= 3 ? parts[1] : null;
        };

        const startsWithIconMap = new Map<string, string>([
            ['PT-', 'pendleLogo.png'],
        ]);

        const exactMatchIconMap = new Map<string, string>([
            ['USD0++', 'USD0++.png'],
            ['USR (Resolv USD)', 'USRUSDC.png'],
            ['ARKISHYPE', 'arkisHYPE.png'],
            ['USD0USD0++', 'USD0USD0++.png'],
            ['USD3', 'USD3.png'],
            ['USD', 'USD.png'],
            ['PENDLE', 'PENDLE.png'],
            ['PUMPBTC', 'pumpBTC.png'],
            ['TETH', 'tETH.png'],
            ['KSUSDE', 'KsUSDe.png'],
            ['KUSDE', 'KUSDe.png'],
        ]);

        const crvAssets = [
            'CRVFRAX',
            'CRVUSDETHCRV',
            'STECRV',
            'CRV',
            'CVXCRV',
            '3CRV',
            'CRV3CRYPTO',
            'CRVCRVUSDTBTCWSTETH',
            'CRVC99RVUSDTBTCWSTETH',
        ];

        (async () => {
            try {
                // Handle special cases first - these are specific asset names that have unique icons
                if (assetName.startsWith('LP-SUSDE')) { 
                    const response = await import(`@blink/assets/src/icons/assetsSvg/LP-SUSDE.svg`);
                    if (!canceled) setIcon(response.default);
                    return;
                }

                if (assetName.startsWith('LP-')) {
                    const baseToken = getBaseTokenFromLP(assetName);
                    if (baseToken) {
                        try {
                            const response = await import(`@blink/assets/src/icons/assetsSvg/${baseToken}.svg`);
                            if (!canceled) setIcon(response.default);
                            return;
                        } catch {
                            try {
                                const response = await import(`@blink/assets/src/icons/assets/${baseToken}.png`);
                                if (!canceled) setIcon(response.default);
                                return;
                            } catch {
                                const fallback = await import(`@blink/assets/src/icons/assetsSvg/TKN.svg`);
                                if (!canceled) setIcon(fallback.default);
                            }
                        }
                    }
                }

                for (const [prefix, filename] of startsWithIconMap) {
                    if (assetName.startsWith(prefix)) {
                        const response = await import(`@blink/assets/src/icons/assets/${filename}`);
                        if (!canceled) setIcon(response.default);
                        return;
                    }
                }

                const exactMatch = exactMatchIconMap.get(assetName);
                if (exactMatch) {
                    const response = await import(`@blink/assets/src/icons/assets/${exactMatch}`);
                    if (!canceled) setIcon(response.default);
                    return;
                }

                if (crvAssets.includes(assetName)) {
                    const response = await import(`@blink/assets/src/icons/assetsSvg/CRV-UNIVERSAL.svg`);
                    if (!canceled) setIcon(response.default);
                    return;
                }

                try {
                    const response = await import(`@blink/assets/src/icons/assetsSvg/${assetName}.svg`);
                    if (!canceled) setIcon(response.default);
                } catch {
                    try {
                        const response = await import(`@blink/assets/src/icons/assets/${assetName}.png`);
                        if (!canceled) setIcon(response.default);
                    } catch {
                        const fallback = await import(`@blink/assets/src/icons/assetsSvg/TKN.svg`);
                        if (!canceled) setIcon(fallback.default);
                    }
                }
            } catch {
                return;
            }
        })();

        return () => {
            canceled = true;
        };
    }, [assetName]);

    return icon;
};
